import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Upload, Send, FileText, CheckCircle, X, Mail } from 'lucide-react';

const ContactSection = () => {
  // Visual state management
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '', 
    timeline: '',
    file: null,
  });
  const [fileName, setFileName] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Check for "?success=true" in the URL when the page loads
  useEffect(() => {
    // Only run on client side
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('success') === 'true') {
        setShowSuccess(true);
        // Clean up the URL so the popup doesn't appear on refresh
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, file }));
      setFileName(file.name);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#FAF8F5] relative">
      
      {/* SUCCESS POPUP MODAL */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full relative animate-in fade-in zoom-in duration-300">
            {/* Close Button */}
            <button 
              onClick={() => setShowSuccess(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>

            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              
              <h3 className="font-playfair text-2xl text-[#3D3D3D]">
                Message Sent!
              </h3>
              
              <p className="font-inter text-sm text-[#5A5A5A] leading-relaxed">
                Thank you for reaching out. I have received your details (and files) and will get back to you shortly.
              </p>

              <button
                onClick={() => setShowSuccess(false)}
                className="mt-4 px-8 py-3 bg-[#3D3D3D] text-[#FAF8F5] font-inter text-sm tracking-widest uppercase hover:bg-[#5A5A5A] transition-colors w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="font-inter text-xs tracking-[0.3em] uppercase text-[#8B7355]">
            Get in Touch
          </span>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-light text-[#3D3D3D] mt-4">
            Start a Project
          </h2>
          <div className="w-12 h-[1px] bg-[#8B7355] mx-auto mt-6" />
          <p className="font-inter text-sm text-[#5A5A5A] mt-6 max-w-xl mx-auto">
            Ready to bring your vision to life? Share your project details and
            let's discuss how we can collaborate.
          </p>
          
          {/* UPDATED: Direct Email Link */}
          <div className="mt-8 flex justify-center">
            <a 
              href="mailto:divyanka.nift@gmail.com" 
              className="flex items-center gap-2 px-6 py-3 bg-white border border-[#E8E2DB] rounded-full hover:border-[#8B7355] hover:text-[#8B7355] transition-all duration-300 group shadow-sm"
            >
              <Mail size={16} className="text-[#8B7355]" />
              <span className="font-inter text-sm text-[#5A5A5A] group-hover:text-[#8B7355]">
                Or email directly: divyanka.nift@gmail.com
              </span>
            </a>
          </div>
        </div>

        {/* Contact Form - FORMSUBMIT.CO */}
        <form
          /* UPDATED: Sends to the activated email */
          action="https://formsubmit.co/divyanka.nift@gmail.com"
          method="POST"
          encType="multipart/form-data"
          className="space-y-6 bg-white p-8 lg:p-12 shadow-sm"
        >
          {/* Disable Captcha */}
          <input type="hidden" name="_captcha" value="false" />
          
          {/* Redirects to URL with ?success=true signal */}
          <input type="hidden" name="_next" value="https://divyankakarmakar.com/?success=true" />

          <div className="grid md:grid-cols-2 gap-6">
            {/* Name */}
            <div className="space-y-2">
              <Label
                htmlFor="name"
                className="font-inter text-xs tracking-widest uppercase text-[#5A5A5A]"
              >
                Name <span className="text-[#8B7355]">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your full name"
                className="bg-[#FAF8F5] border-[#E8E2DB] focus:border-[#8B7355] focus:ring-[#8B7355] font-inter text-sm text-[#3D3D3D] placeholder:text-[#A8A8A8] h-12"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="font-inter text-xs tracking-widest uppercase text-[#5A5A5A]"
              >
                Email <span className="text-[#8B7355]">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your@email.com"
                className="bg-[#FAF8F5] border-[#E8E2DB] focus:border-[#8B7355] focus:ring-[#8B7355] font-inter text-sm text-[#3D3D3D] placeholder:text-[#A8A8A8] h-12"
              />
            </div>
          </div>

          {/* Project Description */}
          <div className="space-y-2">
            <Label
              htmlFor="message"
              className="font-inter text-xs tracking-widest uppercase text-[#5A5A5A]"
            >
              Project Description <span className="text-[#8B7355]">*</span>
            </Label>
            <Textarea
              id="message"
              name="message" 
              required
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Tell me about your project, brand vision, and goals..."
              rows={5}
              className="bg-[#FAF8F5] border-[#E8E2DB] focus:border-[#8B7355] focus:ring-[#8B7355] font-inter text-sm text-[#3D3D3D] placeholder:text-[#A8A8A8] resize-none"
            />
          </div>

          {/* Timeline */}
          <div className="space-y-2">
            <Label
              htmlFor="timeline"
              className="font-inter text-xs tracking-widest uppercase text-[#5A5A5A]"
            >
              Estimated Timeline
            </Label>
            <Input
              id="timeline"
              name="timeline"
              type="text"
              value={formData.timeline}
              onChange={handleInputChange}
              placeholder="e.g., Q3 2025, 3-6 months, ASAP"
              className="bg-[#FAF8F5] border-[#E8E2DB] focus:border-[#8B7355] focus:ring-[#8B7355] font-inter text-sm text-[#3D3D3D] placeholder:text-[#A8A8A8] h-12"
            />
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label
              htmlFor="file"
              className="font-inter text-xs tracking-widest uppercase text-[#5A5A5A]"
            >
              Briefs / Moodboards
            </Label>
            <div className="relative">
              <input
                id="file"
                name="attachment"
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div className="flex items-center gap-4 p-4 border border-dashed border-[#C4B5A4] bg-[#FAF8F5] hover:bg-[#F5F1EC] transition-colors">
                {fileName ? (
                  <>
                    <FileText size={24} className="text-[#8B7355]" />
                    <span className="font-inter text-sm text-[#3D3D3D]">
                      {fileName}
                    </span>
                  </>
                ) : (
                  <>
                    <Upload size={24} className="text-[#8B7355]" />
                    <span className="font-inter text-sm text-[#5A5A5A]">
                      Click or drag to upload files (PDF, DOC, Images, ZIP)
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Expertise Note */}
          <div className="bg-[#F5F1EC] p-4 border-l-2 border-[#8B7355]">
            <p className="font-inter text-xs text-[#5A5A5A] leading-relaxed">
              <span className="font-medium text-[#3D3D3D]">
                Expertise in Vendor Management & Production Oversight
              </span>
              <span className="mx-2">—</span>
              I offer comprehensive support from design concept through
              manufacturing, ensuring quality and timely delivery for
              international clients.
            </p>
          </div>

          {/* Simple Submit Button */}
          <button
            type="submit"
            className="w-full h-14 bg-[#3D3D3D] text-[#FAF8F5] font-inter text-sm tracking-widest uppercase hover:bg-[#5A5A5A] transition-colors duration-300 flex items-center justify-center gap-3"
          >
            <Send size={18} />
            Submit Inquiry
          </button>
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
